'use client';

import Link from '@/components/_rebrand/Link';
import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import { FC } from 'react';

export type UseCaseIntegrationsProps = {
  title: React.ReactNode;
  description: string;
  links?: ButtonWithLinkOrAction[];
  media: React.ReactNode;
  dropShadow: React.ReactNode;
  showButton: boolean;
}

const UseCaseIntegrations: FC<UseCaseIntegrationsProps> = ({ title, description, links = [], media, dropShadow, showButton }) => {
  const { modalActionHandler } = useModalButtonActions();

  const header = (
    <div className="flex flex-col md:items-center gap-2 md:gap-3 md:text-center">
      <h2 className="font-500 tracking-tight text-30/40 md:text-36/42 text-violet-200">{title}</h2>
      <p className="text-17/24 max-w-80 text-pretty text-violet-200">{description}</p>
      {showButton && (
        <ul className="flex gap-1 md:gap-2 justify-center mt-2">
          {links.map((link, index) => {
            return (
              <li key={index}>
                {link.href.length ? (
                  <Link
                    key={index}

                    className={cn('block button button-size-medium', {
                      'button-primary-violet': link.type === 'primary',
                      'button-secondary-violet-300': link.type === 'secondary',
                    })}
                    href={link.href}
                  >
                    {link.title}
                  </Link>
                ) : (
                  <button
                    key={index}
                    className={cn('block button button-size-medium', {
                      'button-primary-violet': link.type === 'primary',
                      'button-secondary-violet-300': link.type === 'secondary',
                    })}
                    onClick={() => {
                      modalActionHandler(link.action);
                    }}
                  >
                    {link.title}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div className="mx-2 relative overflow-hidden">
      <div className={`bg-violet-900 rounded-[24px] px-container-mobile md:px-container-desktop py-4 md:py-15 ${showButton ? "md:min-h-[810px]" : "md:min-h-[740px]"} ${showButton ? "lg:min-h-[980px]" : "lg:min-h-[890px]"} min-h-auto`}>
        <div className="max-w-grid mx-auto flex flex-col gap-y-3 z-10 relative md:absolute ml-auto mr-auto left-0 right-0 text-center">
          <div className="grid grid-cols-12 gap-x-0 md:gap-x-gutter-desktop">
            <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 xl:col-span-10 xl:col-start-2">
              {header}
            </div>
          </div>
          <div className="grid grid-cols-12 gap-x-0 md:gap-x-gutter-desktop">
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <div className="mx-[-5%]">{media}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-[-5%] absolute bottom-[-300px]">
        {dropShadow}
      </div>
    </div>
  );
};

export default UseCaseIntegrations;
